// Database Models

export interface Profile {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    postal_code?: string;
    country?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: string;
  producer_id?: string;
  name: string;
  description?: string;
  price: number;
  image_urls?: string[];
  barcode?: string;
  inventory_count?: number;
  stock_level?: number;
  created_at: string;
  updated_at: string;
  producer?: {
    id: string;
    company_name: string;
  };
}

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  price_at_time?: number;
  product: Product;
  status?: 'pending' | 'picked' | 'preparing';
  created_at: string;
  updated_at: string;
}

// Inline Edit Error Classes (Feature 006)
export class ValidationError extends Error {
  field: string;
  availableValue?: number;

  constructor(message: string, field: string, availableValue?: number) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.availableValue = availableValue;
  }
}

export class ConflictError extends Error {
  updatedData: OrderItem;

  constructor(message: string, updatedData: OrderItem) {
    super(message);
    this.name = 'ConflictError';
    this.updatedData = updatedData;
  }
}

export class NetworkError extends Error {
  originalError: Error;

  constructor(message: string, originalError: Error) {
    super(message);
    this.name = 'NetworkError';
    this.originalError = originalError;
  }
}

// Inline Edit State Types (Feature 006)
export type InlineEditStatus = 'idle' | 'editing' | 'saving' | 'success' | 'error';

export interface InlineEditState {
  status: InlineEditStatus;
  currentValue: string;
  queuedValue: string | null;
  error: string | null;
}

export interface OrderHistory {
  id: string;
  order_id: string;
  status: string;
  notes?: string;
  user_email?: string;
  created_at: string;
}

export interface Order {
  id: string;
  user_id: string;
  display_id?: number;
  status: string;
  total_amount: number;
  created_at: string;
  updated_at: string;
  profile?: Profile;
  order_items: OrderItem[];
  order_history?: OrderHistory[];
  guest_email?: string;
  guest_details?: string | {
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  };
  address?: string;
  profiles?: Profile; // For compatibility with Supabase nested responses
}

export interface Producer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  created_at: string;
  updated_at: string;
}

// Api Request/Response types

export interface CreateOrderRequest {
  items: {
    product_id: string;
    quantity: number;
  }[];
  profile?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    address?: {
      street?: string;
      city?: string;
      postal_code?: string;
      country?: string;
    };
  };
}

export interface UpdateOrderRequest {
  status?: string;
  items?: {
    id?: string;
    product_id: string;
    quantity: number;
  }[];
  profile?: Profile;
}

export interface ProducerSalesItem {
  order_id: string;
  order_date: string;
  order_status: string;
  product_id: string;
  product_name: string;
  quantity: number;
  price_at_time: number;
  total_amount: number;
} 